import React, { useEffect } from 'react'
import { Field } from 'formik'
import { Grid } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import { makeStyles, Theme } from '@material-ui/core/styles'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import * as yup from 'yup'
import ValidationWarnings from '@src/routes/Configurators/validations/ValidationWarnings'
import { joinMessagesIntoOne, splitMessages } from '../utils'
import useFormikHelper from '../useFormikHelper'
import useTubeValidation from '../useTubeValidation'
import { TubeProperties } from '../../../utils/StandardMTOEnum'
import {
  getOligoNameLabel,
  SequenceType,
  useProductFromQueryParam,
} from '../ProductsConfiguration'
import { useDebounce } from 'react-use'
import { BiTubeProduct } from '../../BiTube/enum'

const useStyles = makeStyles((theme: Theme) => {
  return {
    nameLabel: {
      '& label': {
        fontSize: theme.typography.pxToRem(14),
        display: 'flex',
        alignItems: 'center',
        '& svg': {
          fontSize: theme.typography.pxToRem(19.2),
        },
      },
    },

    oligoNameInfo: {
      fontWeight: theme.typography.fontWeightRegular,
    },
  }
})
interface OligoNameProps {
  isAntiSense?: boolean
  disabled?: boolean
  autoGenerateName?: (e: string) => void
}

const validNameRegEx = /^[a-zA-Z0-9_#*().,+.[\-\]/ ]*$/
const OligoName: React.FC<OligoNameProps> = ({
  isAntiSense,
  disabled = false,
  autoGenerateName,
}) => {
  const { validationAPI } = useTubeValidation()
  const classes = useStyles()
  const intl = useIntl()
  const formik = useFormikHelper()
  const product = useProductFromQueryParam()

  const nameType = isAntiSense
    ? TubeProperties.AntiSenseName
    : TubeProperties.Name

  const sequenceType = isAntiSense
    ? SequenceType.Secondary
    : SequenceType.Primary

  const validateName = (value: string) => {
    let splChar = ''
    if (value) {
      value.split('').forEach((c) => {
        if (!c.match(validNameRegEx) && !splChar.includes(c)) {
          if (splChar === '') {
            splChar = c
          } else {
            splChar = splChar + ', ' + c
          }
        }
      })
    }
    const validator = yup
      .string()
      .trim()
      .required(
        intl.formatMessage({
          id: 'REQUIRE_FIELD',
          defaultMessage: 'Required Field',
        })
      )
      .max(
        TubeProperties.NameMaxLength,
        intl.formatMessage({
          id: 'OLIGO_NAME_MAX_LENGTH_VALIDATION_TEXT',
          defaultMessage:
            'Maximum oligo name length is ' + TubeProperties.NameMaxLength,
        })
      )
      .matches(
        validNameRegEx,
        intl.formatMessage({
          id: 'OLIGO_NAME_SPECIAL_CHARACTER_TEXT',
          defaultMessage: 'Name contains invalid character(s): ' + splChar,
        })
      )
    const errors: string[] = []
    try {
      validator.validateSync(value, { abortEarly: false })
    } catch (error: any) {
      for (const err of error.errors) {
        errors.push(err)
      }
      formik.setFieldError(nameType, joinMessagesIntoOne(errors))
    }

    if (errors.length === 0) {
      const updatedValidationFields = {
        ...formik.validationFields,
        [nameType]: value,
      }
      // backend call only when total validation is active, if all valid, this changes display page error to false
      formik.selectedOligoValues.displayPageError &&
        validationAPI(updatedValidationFields)
      formik.setFieldError(nameType, '')
    }
    return null
  }
  useEffect(() => {
    if (
      formik.selectedOligoValues?.[nameType] &&
      formik.selectedOligoValues?.[nameType] !== '' &&
      !formik.touched?.[nameType]
    ) {
      formik.setFieldTouched(nameType, true, false)
      validateName(formik.selectedOligoValues?.[nameType] || '')
    }
  }, [])
  const onNameChange = (event) => {
    const { value } = event.target
    formik.setFieldTouched(nameType, true, false)
    formik.setOligoFieldValue(nameType, value, false)
    validateName(value)
    return null
  }

  //update autogenerate if enabled
  useDebounce(
    async () => {
      if (
        !isAntiSense &&
        formik?.selectedOligoValues[nameType] !== '' &&
        formik.selectedOligoValues?.senseNameCheck
      ) {
        autoGenerateName &&
          autoGenerateName(formik?.selectedOligoValues[nameType] || '')
      }
    },
    500,
    [formik.selectedOligoValues[nameType]]
  )
  const errorList =
    (formik.touched?.[nameType] &&
      formik.errors?.[nameType] &&
      splitMessages(formik.errors?.[nameType] || '')) ||
    []

  return (
    <Grid container>
      <Grid item xs={6}>
        <Field
          fieldId={`oligo_${nameType}_id`}
          name={formik.oligoFieldName(nameType)}
          component={LiquidInputAdapter}
          value={formik.selectedOligoValues[nameType] || ''}
          rootClass={classes.nameLabel}
          onChange={onNameChange}
          displayError={false}
          required
          toolTipText={
            product === BiTubeProduct.mirna ? (
              <FormattedMessage
                id="CONF_MIRNA_OLIGO_NAME_TOOLTIP"
                defaultMessage="Maximum character limit for technical datasheet is 28. Character size varies and the product name prefix on the tube label may be truncated if longer than 18 characters."
              />
            ) : (
              <FormattedMessage
                id="CONF_DNA_OLIGO_NAME_TOOLTIP"
                defaultMessage="Maximum character limit for technical datasheet is 30. Character size varies and the product name prefix on the tube label may be truncated if longer than 18 characters."
              />
            )
          }
          label={getOligoNameLabel(product, sequenceType)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} container>
        <Grid item>
          {errorList.length > 0 && (
            <ValidationErrors errors={errorList} configuratorsRedesign />
          )}
        </Grid>
      </Grid>
      {(formik.selectedOligoValues[nameType]?.trim() || '').length > 18 ? (
        <Grid item xs={12}>
          <ValidationWarnings
            warnings={[
              intl.formatMessage({
                id: 'OLIGO_NAME_WARNING_TEXT',
                defaultMessage:
                  'Character size varies and the product name prefix on the tube label may be truncated if longer than 18 characters.',
              }),
            ]}
          />
        </Grid>
      ) : (
        ''
      )}
    </Grid>
  )
}

export default OligoName
